import { t } from "../../i18n";
import { useState } from "react";
import type { StructureType, WallInput } from "@tasduvar/engine";
import { useProjectStore } from "../../state/projectStore";
import { runAnalysis } from "../../state/useAnalysis";

/** Durum işareti: dolu kare = sağlıyor, boş kare = sağlamıyor, çarpı = hesap hatası (renk tek başına taşıyıcı değil). */
function StatusMark({ wall }: { wall: WallInput }): JSX.Element {
  const a = runAnalysis(wall);
  const ok = a.result?.ok ?? false;
  const title = a.error ? t("Hesap hatası") : ok ? t("Tüm kontroller sağlanıyor") : t("Sağlanmayan kontrol var");
  return (
    <svg width="10" height="10" viewBox="0 0 10 10" aria-label={title} role="img" className="shrink-0">
      <title>{title}</title>
      {a.error ? <path d="M1 1 L9 9 M9 1 L1 9" stroke="rgb(var(--c-bad))" strokeWidth="1.6" /> : ok ? <rect x="1" y="1" width="8" height="8" fill="rgb(var(--c-ok))" /> : <rect x="1.5" y="1.5" width="7" height="7" fill="none" stroke="rgb(var(--c-bad))" strokeWidth="1.5" />}
    </svg>
  );
}

export function WallList({ variant = "paper" }: { variant?: "paper" | "cad" } = {}): JSX.Element {
  const { project, selectedWallId, selectWall, addWall, copyWall, removeWall, renameWall, readOnly } = useProjectStore();
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [adding, setAdding] = useState(false);
  if (!project) return <></>;
  const TYPES: { type: StructureType; label: string; hint: string }[] = [
    { type: "masonry-gravity", label: t("Taş ağırlık duvarı"), hint: t("Harçlı taş / beton; kademeli gövde") },
    { type: "rc-cantilever", label: t("Betonarme konsol duvar"), hint: t("T/L kesit; kesit tasarımı ve donatı") },
    { type: "rc-counterfort", label: t("Betonarme payandalı duvar"), hint: t("Konsol + payandalar") },
  ];
  return (
    <nav aria-label={t("Duvarlar")} className={variant === "cad" ? "flex flex-col min-h-0 bg-cad-panel" : "w-56 shrink-0 bg-sheet border-r border-rule flex flex-col"}>
      <div className={variant === "cad" ? "cad-head" : "flex items-center justify-between pl-4 pr-2 h-11 border-b border-rule"}>
        <span className={variant === "cad" ? "" : "text-[13px] font-medium text-ink"}>{t("Duvarlar")} {variant === "cad" ? "— " : ""}<span className="num text-stone">{project.walls.length}</span></span>
        {!readOnly ? <button type="button" className={variant === "cad" ? "ml-auto normal-case tracking-normal font-normal text-[11px] text-cad-ink3 hover:text-cad-ink cursor-pointer" : "btn-ghost !px-2 !py-1 !text-xs"} onClick={() => setAdding((v) => !v)} aria-label={t("Duvar ekle")} aria-expanded={adding}>{t("Ekle")}</button> : null}
      </div>
      {adding ? (
        <div role="menu" aria-label={t("Duvar tipi")} className="border-b border-rule bg-page px-2 py-1.5 grid gap-1">
          {TYPES.map((x) => (
            <button key={x.type} type="button" role="menuitem" className="text-left rounded px-2 py-1 hover:bg-ink-light" onClick={() => { addWall(x.type); setAdding(false); }}>
              <div className="text-[12px] font-medium text-ink">{x.label}</div><div className="text-[10px] text-stone">{x.hint}</div>
            </button>
          ))}
        </div>
      ) : null}
      <ul className="flex-1 overflow-auto py-1">
        {project.walls.map((w) => (
          <li key={w.id} className={`group flex items-center gap-2.5 pl-4 pr-2 py-2 text-sm cursor-pointer border-l-2 transition-colors duration-150 ${w.id === selectedWallId ? "border-ink bg-ink-light text-ink font-medium" : "border-transparent hover:bg-page"}`} onClick={() => selectWall(w.id)}>
            <StatusMark wall={w} />
            {w.geometry.mode === "rc-cantilever" ? <span className="text-[9px] uppercase tracking-wide text-stone border border-rule rounded px-1" title={w.geometry.counterforts ? t("Betonarme payandalı duvar") : t("Betonarme konsol duvar")}>BA</span> : null}
            {editing?.id === w.id ? (
              <input autoFocus className="input !py-0.5 font-sans" value={editing.name} onChange={(e) => setEditing({ id: w.id, name: e.target.value })}
                onBlur={() => { renameWall(w.id, editing.name); setEditing(null); }}
                onKeyDown={(e) => { if (e.key === "Enter") { renameWall(w.id, editing.name); setEditing(null); } if (e.key === "Escape") setEditing(null); }} />
            ) : (
              <span className="flex-1 truncate" onDoubleClick={() => { if (!readOnly) setEditing({ id: w.id, name: w.name }); }} title={readOnly ? undefined : t("Yeniden adlandırmak için çift tıklayın")}>{w.name}</span>
            )}
            {readOnly ? null : <span className="hidden group-hover:flex gap-0.5">
              <button type="button" className="text-stone hover:text-ink px-1 cursor-pointer" onClick={(e) => { e.stopPropagation(); copyWall(w.id); }} aria-label={`${w.name} kopyala`} title={t("Kopyala")}>⧉</button>
              <button type="button" className="text-stone hover:text-bad px-1 cursor-pointer disabled:opacity-40" disabled={project.walls.length <= 1} onClick={(e) => { e.stopPropagation(); if (window.confirm(`"${w.name}" silinsin mi?`)) removeWall(w.id); }} aria-label={`${w.name} sil`} title={t("Sil")}>✕</button>
            </span>}
          </li>
        ))}
      </ul>
    </nav>
  );
}
